// scheduling/utils/genTrack.js
// Gen Track balance — the ONE place that turns hours into a percentage and a band.
//
// Shared deliberately: the Reporting tab's table and the "Needs more Tax / Needs more
// Assurance" badge on Staff Suggestions both call these. If the calc lived in each
// surface they would eventually disagree about who is out of balance, and the badge
// would be telling a scheduler the opposite of the report.
//
// The server (api/rprsReports/gentrack.js) returns HOURS ONLY and owns the service-code
// lists, because it does the aggregation. This file owns the thresholds, because they
// are presentation. Neither list is duplicated.

// DJB: "60/40 either way is considered in balance. 50/50 is a pipe dream."
// So balanced is 40–60% tax INCLUSIVE; outside that is a lean, not a failure.
export const BAND_LOW_PCT = 40;
export const BAND_HIGH_PCT = 60;

// Below these, we report the hours but refuse to judge the ratio.
//
// A percentage computed off a handful of hours is noise dressed as a finding: someone
// with 4 forward hours on one audit reads "0% tax", which looks like a scheduling
// problem and is actually a rounding artefact. One week of charged time and one day of
// scheduled time are the smallest amounts that can carry any signal.
export const MIN_ACTUAL_HOURS = 40;
export const MIN_FORWARD_HOURS = 8;

export const BANDS = Object.freeze({
  BALANCED: 'balanced',
  NEEDS_TAX: 'needs_tax',
  NEEDS_ASSURANCE: 'needs_assurance',
  UNKNOWN: 'unknown',          // not enough hours to say
});

export const BAND_META = Object.freeze({
  [BANDS.BALANCED]:        { label: 'In balance',      short: 'Balanced',   bg: '#F0FDF4', fg: '#15803D', bar: '#22C55E' },
  [BANDS.NEEDS_TAX]:       { label: 'Needs more Tax',  short: 'Needs Tax',  bg: '#FFFBEB', fg: '#B45309', bar: '#F59E0B' },
  [BANDS.NEEDS_ASSURANCE]: { label: 'Needs more Assurance', short: 'Needs Assurance', bg: '#EFF6FF', fg: '#1D4ED8', bar: '#3B82F6' },
  [BANDS.UNKNOWN]:         { label: 'Not enough hours', short: '—',        bg: '#F9FAFB', fg: '#9CA3AF', bar: '#D1D5DB' },
});

/**
 * Tax share of tax+assurance, 0–100, or null when there is nothing to divide.
 *
 * "Other" hours (ADMIN, PTO, ACCTG, …) are deliberately NOT in the denominator — the
 * question is how a person's CLIENT work splits, and for Gen Track the excluded bucket
 * is ~31% admin and PTO. Including it would make everyone look tax-starved.
 */
export function ratioPct(taxHours, assuranceHours) {
  const t = Number(taxHours) || 0;
  const a = Number(assuranceHours) || 0;
  const total = t + a;
  if (total <= 0) return null;
  return Math.round((100 * t) / total);
}

/**
 * Band for a tax percentage, gated on having enough hours to mean anything.
 *
 * @param {number|null} pct       from ratioPct
 * @param {number} totalHours     tax + assurance, for the significance gate
 * @param {number} minHours       MIN_ACTUAL_HOURS or MIN_FORWARD_HOURS
 */
export function bandOf(pct, totalHours, minHours = MIN_ACTUAL_HOURS) {
  if (pct == null || (Number(totalHours) || 0) < minHours) return BANDS.UNKNOWN;
  if (pct < BAND_LOW_PCT) return BANDS.NEEDS_TAX;
  if (pct > BAND_HIGH_PCT) return BANDS.NEEDS_ASSURANCE;
  return BANDS.BALANCED;
}

/**
 * Decorate one server row with the three ratios and their bands.
 *
 * The three columns answer three different questions and are not interchangeable:
 *   actual     what they have done          (the historical fact)
 *   forward    what they are scheduled for  (SEASONAL — see below)
 *   projected  actual + forward             (where they land if nothing changes)
 *
 * `projected` is the one to act on. `forward` on its own is structurally season-
 * dependent — in September almost every Gen Track staffer is scheduled onto audits, so
 * a low forward tax % is usually a statement about the calendar, not the person.
 */
export function decorateRow(row) {
  const actualTotal = (row.taxHours || 0) + (row.assurHours || 0);
  const fwdTotal = (row.fwdTax || 0) + (row.fwdAssur || 0);
  const projTax = (row.taxHours || 0) + (row.fwdTax || 0);
  const projAssur = (row.assurHours || 0) + (row.fwdAssur || 0);
  const projTotal = projTax + projAssur;

  const actualPct = ratioPct(row.taxHours, row.assurHours);
  const forwardPct = ratioPct(row.fwdTax, row.fwdAssur);
  const projectedPct = ratioPct(projTax, projAssur);

  return {
    ...row,
    actualTotal,
    fwdTotal,
    projTotal,
    actualPct,
    forwardPct,
    projectedPct,
    actualBand: bandOf(actualPct, actualTotal, MIN_ACTUAL_HOURS),
    forwardBand: bandOf(forwardPct, fwdTotal, MIN_FORWARD_HOURS),
    // The headline band. Projected, not actual, because the report exists to answer
    // "are we on the right track to fixing this" — DJB's words.
    band: bandOf(projectedPct, projTotal, MIN_ACTUAL_HOURS),
    // Direction of travel, and the size of the move in points of tax share. Only
    // meaningful once there is enough scheduled ahead to shift anything.
    trend: fwdTotal >= MIN_FORWARD_HOURS ? trendOf(actualPct, projectedPct) : TRENDS.UNKNOWN,
    deltaPct: (actualPct == null || projectedPct == null) ? null : projectedPct - actualPct,
  };
}

/**
 * How far outside the in-balance band a percentage sits, in points. 0 = in balance.
 *
 * This, not the raw percentage, is what "getting better" means here. Tax % rising is
 * good for someone at 20% and bad for someone at 80%, so direction of travel can only
 * be judged against the band.
 */
export function bandDistance(pct) {
  if (pct == null) return null;
  if (pct >= BAND_LOW_PCT && pct <= BAND_HIGH_PCT) return 0;
  return pct < BAND_LOW_PCT ? BAND_LOW_PCT - pct : pct - BAND_HIGH_PCT;
}

export const TRENDS = Object.freeze({
  CLOSER: 'closer',
  FURTHER: 'further',
  STEADY: 'steady',
  UNKNOWN: 'unknown',
});

export const TREND_META = Object.freeze({
  [TRENDS.CLOSER]:  { word: 'closer',  bg: '#F0FDF4', fg: '#15803D', title: 'What is scheduled ahead moves them TOWARD the 40–60% band' },
  [TRENDS.FURTHER]: { word: 'further', bg: '#FFFBEB', fg: '#B45309', title: 'What is scheduled ahead moves them AWAY from the 40–60% band' },
  [TRENDS.STEADY]:  { word: 'steady',  bg: '#F9FAFB', fg: '#6B7280', title: 'What is scheduled ahead does not change their distance from the band' },
  [TRENDS.UNKNOWN]: { word: '—',       bg: 'transparent', fg: '#9CA3AF', title: 'Not enough hours to judge direction' },
});

/**
 * Is the projected split closer to or further from balance than the charged split?
 *
 * This is the literal answer to DJB's ask — "so we can see if we are on the right track
 * to fixing any that are out of balance" — which no single percentage column can give.
 */
export function trendOf(chargedPct, projectedPct) {
  const from = bandDistance(chargedPct);
  const to = bandDistance(projectedPct);
  if (from == null || to == null) return TRENDS.UNKNOWN;
  if (to < from) return TRENDS.CLOSER;
  if (to > from) return TRENDS.FURTHER;
  return TRENDS.STEADY;
}

/** Counts per band over decorated rows, for the summary strip. */
export function tallyBands(rows) {
  const t = { [BANDS.BALANCED]: 0, [BANDS.NEEDS_TAX]: 0, [BANDS.NEEDS_ASSURANCE]: 0, [BANDS.UNKNOWN]: 0 };
  for (const r of rows) t[r.band] = (t[r.band] || 0) + 1;
  return t;
}

/**
 * Counts per direction-of-travel, for the summary strip.
 *
 * Worth surfacing at the top rather than leaving it per-row: measured on prod, 15 of 21
 * Gen Track staff are currently scheduled in the direction that moves them AWAY from
 * balance, which is the single most actionable number in the report and is invisible if
 * you only tally bands.
 */
export function tallyTrends(rows) {
  const t = { [TRENDS.CLOSER]: 0, [TRENDS.FURTHER]: 0, [TRENDS.STEADY]: 0, [TRENDS.UNKNOWN]: 0 };
  for (const r of rows) t[r.trend] = (t[r.trend] || 0) + 1;
  return t;
}

/**
 * Which side does a JOB count toward?
 *
 * Mirrors the CASE in api/rprsReports/gentrack.js, but the code and name lists are
 * PASSED IN from the server response rather than duplicated here — that is the whole
 * reason `gentrack_bands` echoes them back. A second copy of DJB's service lists in
 * client code would eventually disagree with the one the aggregation uses.
 *
 * The ServiceDesc fallback matters: rprs.Jobs.ServiceIndex is NULL on 106 open jobs
 * that do have a ServiceDesc, so a code-only match silently classifies them as neither.
 *
 * @param {object} job    a rprs.Jobs-shaped row (ServiceIndex / ServiceDesc)
 * @param {object} lists  { taxCodes, assuranceCodes, taxDescs, assuranceDescs }
 * @returns {'Tax'|'Assurance'|'Neither'}
 */
export function jobBucketOf(job, lists) {
  if (!job || !lists) return 'Neither';
  const idx = job.ServiceIndex || null;
  const desc = job.ServiceDesc || null;
  const has = (arr, v) => Array.isArray(arr) && v != null && arr.includes(v);

  if (has(lists.taxCodes, idx)) return 'Tax';
  if (has(lists.assuranceCodes, idx)) return 'Assurance';
  if (idx) return 'Neither';                       // a known code that is neither
  if (has(lists.taxDescs, desc)) return 'Tax';
  if (has(lists.assuranceDescs, desc)) return 'Assurance';
  return 'Neither';
}

/**
 * Does taking work in `jobBucket` move this person toward balance?
 *
 * Used by the Staff Suggestions badge, which must only fire when it is a REASON to
 * pick someone. A "needs Tax" badge on a tax job is useful; the same badge on an audit
 * job is noise that trains people to ignore it.
 *
 * @param {object} decorated  a decorateRow() result
 * @param {'Tax'|'Assurance'|'Neither'} jobBucket  which side the job counts toward —
 *        derived from the job's ServiceIndex/ServiceDesc, or, for a custom job, from
 *        rprs.CustomJobs.Category
 */
export function helpsBalance(decorated, jobBucket) {
  if (!decorated) return false;
  if (decorated.band === BANDS.NEEDS_TAX) return jobBucket === 'Tax';
  if (decorated.band === BANDS.NEEDS_ASSURANCE) return jobBucket === 'Assurance';
  return false;
}

/** Hours as the app formats them everywhere: 2 decimals, no trailing zeros. */
export function fmtHours(h) {
  const n = Number(h) || 0;
  return (Math.round(n * 100) / 100).toString();
}
